import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, Loader, Sparkles, CheckCircle, ArrowRight } from 'lucide-react';
import { startAIConversation, sendAIMessage, getAITemplateDetails, generateAIResumeContent } from '../services/api';

const AITemplateRecommender = ({ onTemplatesRecommended, onSelectTemplate }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [recommendedTemplates, setRecommendedTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateDetails, setTemplateDetails] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize conversation
  useEffect(() => {
    const initConversation = async () => {
      try {
        const response = await startAIConversation();
        setMessages([
          {
            role: 'assistant',
            content: response.message,
            timestamp: new Date().toISOString()
          }
        ]);
      } catch (error) {
        console.error('Error starting conversation:', error);
        setMessages([
          {
            role: 'assistant',
            content: "Hi! I'm your AI resume consultant. I'd love to help you find the perfect resume template. Tell me about the role you're looking for and I'll recommend the best templates for you!",
            timestamp: new Date().toISOString()
          }
        ]);
      }
    };

    initConversation();
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to chat
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      // Prepare conversation history for API
      const apiConversationHistory = conversationHistory.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Send message to AI
      const response = await sendAIMessage({
        user_message: userMessage,
        conversation_history: apiConversationHistory,
        question_number: questionNumber
      });

      // Add assistant response to chat
      const assistantMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);

      // Update conversation history
      setConversationHistory(prev => [
        ...prev,
        { role: 'user', content: userMessage },
        { role: 'assistant', content: response.message }
      ]);

      setQuestionNumber(response.question_number);

      // Check if AI has recommendations
      if (response.should_recommend && response.recommended_templates?.length > 0) {
        setRecommendedTemplates(response.recommended_templates);
        
        // Always fetch full template details from database using the recommended template IDs
        // This ensures we have html_template, css_styles, and other database fields
        try {
          const details = await getAITemplateDetails(response.recommended_templates);
          setTemplateDetails(details);
        } catch (detailError) {
          console.error('Error fetching template details:', detailError);
          // Fallback: use custom_templates from response if available
          if (response.custom_templates && response.custom_templates.length > 0) {
            setTemplateDetails(response.custom_templates);
          } else {
            // Last resort: create minimal template objects
            setTemplateDetails(response.recommended_templates.map(id => ({ 
              id, 
              name: `Template ${id}`,
              description: 'Custom AI-generated template'
            })));
          }
        }
        
        if (onTemplatesRecommended) {
          onTemplatesRecommended(response.recommended_templates);
        }
      }

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble processing your request. Let me help you with some general recommendations. Could you tell me what industry you're in?",
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowTemplates = () => {
    console.log('Show templates clicked. Template details:', templateDetails);
    console.log('Recommended templates:', recommendedTemplates);
    setShowTemplates(true);
  };

  const MessageBubble = ({ message }) => {
    const isUser = message.role === 'user';
    const isError = message.error;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start max-w-[80%] gap-2`}>
          {/* Avatar */}
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
            isUser ? 'bg-primary-600' : isError ? 'bg-red-100' : 'bg-gradient-to-br from-purple-500 to-blue-500'
          }`}>
            {isUser ? (
              <User className="w-5 h-5 text-white" />
            ) : (
              <Bot className="w-5 h-5 text-white" />
            )}
          </div>

          {/* Message Content */}
          <div className={`rounded-lg px-4 py-3 ${
            isUser 
              ? 'bg-primary-600 text-white' 
              : isError 
              ? 'bg-red-50 text-red-900 border border-red-200'
              : 'bg-white text-gray-900 shadow-md border border-gray-100'
          }`}>
            <div className="text-sm whitespace-pre-wrap break-words">
              {message.content}
            </div>
            <div className={`text-xs mt-1 ${
              isUser ? 'text-primary-200' : 'text-gray-400'
            }`}>
              {new Date(message.timestamp).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const TemplateCard = ({ template, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-white rounded-lg border-2 border-gray-200 hover:border-primary-500 transition-all duration-200 overflow-hidden group"
    >
      {/* Template Preview */}
      <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center group-hover:from-primary-50 group-hover:to-blue-50 transition-colors">
        <div className="text-center">
          <div className="w-16 h-20 bg-white rounded border-2 border-gray-300 mx-auto mb-2 group-hover:border-primary-500 transition-colors"></div>
          <p className="text-sm text-gray-500">{template.name}</p>
        </div>
      </div>

      {/* Template Info */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2">{template.name}</h3>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{template.description}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {template.best_for?.slice(0, 3).map((tag, idx) => (
            <span key={idx} className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
              {tag}
            </span>
          ))}
        </div>

        {/* Features */}
        <div className="space-y-1 mb-4">
          {template.features?.slice(0, 3).map((feature, idx) => (
            <div key={idx} className="flex items-center text-xs text-gray-600">
              <CheckCircle className="w-3 h-3 text-green-500 mr-1" />
              {feature}
            </div>
          ))}
        </div>

        <button
          onClick={async () => {
            if (onSelectTemplate) {
              // Generate resume content from conversation before selecting template
              try {
                const resumeContent = await generateAIResumeContent(conversationHistory);
                onSelectTemplate(template, resumeContent, conversationHistory);
              } catch (error) {
                console.error('Error generating resume content:', error);
                // Still proceed with template selection even if resume generation fails
                onSelectTemplate(template, null, conversationHistory);
              }
            }
          }}
          className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center text-sm"
        >
          Use This Template
          <ArrowRight className="w-4 h-4 ml-2" />
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full mb-4"
          >
            <Sparkles className="w-8 h-8 text-white" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl font-bold text-gray-900 mb-2"
          >
            AI Resume Consultant
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-600"
          >
            Let our AI help you find the perfect resume template
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat Interface */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col h-[600px]">
              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                <AnimatePresence>
                  {messages.map((message, index) => (
                    <MessageBubble key={index} message={message} />
                  ))}
                </AnimatePresence>

                {/* Loading Indicator */}
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-2 text-gray-500"
                  >
                    <Loader className="w-5 h-5 animate-spin" />
                    <span className="text-sm">AI is thinking...</span>
                  </motion.div>
                )}

                {/* Show Templates Button */}
                {recommendedTemplates.length > 0 && !showTemplates && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-center mt-6"
                  >
                    <button
                      onClick={handleShowTemplates}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 flex items-center shadow-lg"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Show My Recommended Templates
                    </button>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="border-t border-gray-200 p-4 bg-white">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Type your message..."
                    disabled={isLoading}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <button
                    type="submit"
                    disabled={!inputMessage.trim() || isLoading}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Info Panel / Template Preview */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-xl p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Sparkles className="w-5 h-5 text-purple-500 mr-2" />
                How It Works
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-semibold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm mb-1">Answer Questions</h4>
                    <p className="text-gray-600 text-xs">Share your career details with our AI consultant</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm mb-1">Get Recommendations</h4>
                    <p className="text-gray-600 text-xs">AI analyzes your profile and suggests perfect templates</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-semibold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm mb-1">Choose & Customize</h4>
                    <p className="text-gray-600 text-xs">Select your favorite template and start building</p>
                  </div>
                </div>
              </div>

              {/* Progress Indicator */}
              {questionNumber > 1 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm text-gray-500">
                      Question {Math.min(questionNumber - 1, 5)} of 5
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <motion.div
                      className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(((questionNumber - 1) / 5) * 100, 100)}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recommended Templates Section */}
        {showTemplates && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8"
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Your Personalized Template Recommendations
              </h2>
              <p className="text-gray-600">
                Based on our conversation, here are the best templates for you
              </p>
            </div>

            {templateDetails.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templateDetails.map((template, index) => (
                  <TemplateCard 
                    key={template.id || template.database_id || index} 
                    template={template} 
                    index={index} 
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-lg">
                <p className="text-gray-600 mb-4">
                  Loading template details...
                </p>
                <button
                  onClick={async () => {
                    // Retry fetching template details
                    try {
                      const details = await getAITemplateDetails(recommendedTemplates);
                      setTemplateDetails(details);
                    } catch (error) {
                      console.error('Error fetching templates:', error);
                      alert('Unable to load templates. Please try again.');
                    }
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Retry Loading Templates
                </button>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AITemplateRecommender;

